import React, { useMemo, useState } from "react";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { Asset_ExistsResult } from "@safetyculture/s12-apis-connect-web/s12/iot/v1/asset_pb";
import { GetContractsByOrgResponse } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { GetSubscriptionResponse } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import {
  GetOrgInspectionQuotaResponse,
  GetOrganisationUserListResponse,
  Pagination,
  Role,
  RoleDetails,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { GetSubscriptionSeatUsageResponse } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_seats_pb";
import { UserStatus } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import {
  AdminOnlyFilterContext,
  ContractsListContext,
  IsOrgAndSeatDataLoadingContext,
  OrgDataContext,
  OrgDetailsContext,
  OrgHasSensorsContext,
  OrgInspectionQuotaContext,
  OrgUsersList,
  OrgUsersListContext,
  SubDataContext,
  SubscriptionSeatUsageContext,
  UserQueryFilterContext,
  UserSeatTypeFilterContext,
  UserStatusFilterContext,
} from "./organisation-context";
import {
  DEFAULT_TABLE_PAGINATION,
  USER_LIST_PREFERENCE_KEY,
} from "constants/index";

interface OrgUsersListContextWrapperProps {
  children: React.ReactNode;
}

interface OrganisationContextWrapperProps {
  children: React.ReactNode;
}

// User list tab information and filters
const OrgUsersListContextWrapper = ({
  children,
}: OrgUsersListContextWrapperProps) => {
  const [orgUsersList, setOrgUsersList] = useState<OrgUsersList>({
    usersListResponse: new GetOrganisationUserListResponse({
      users: [],
      totalItems: 0,
    }),
    pagination: new Pagination({
      pageSize: DEFAULT_TABLE_PAGINATION,
      offset: 0,
    }),
    isLoading: false,
  });
  const prefersAdminsOnlyFilter = useMemo(() => {
    if (localStorage.getItem(USER_LIST_PREFERENCE_KEY) === "false") {
      return false;
    }

    return true;
  }, []);

  const [adminOnlyFilter, setAdminOnlyFilter] = useState<boolean>(
    prefersAdminsOnlyFilter
  );
  const [userQueryFilter, setUserQueryFilter] = useState<string>("");

  const userStatusPref = useMemo(() => {
    const data = window.localStorage.getItem("user_query_pref");
    if (data !== null) return JSON.parse(data);
  }, []);

  const [userStatusFilter, setUserStatusFilter] = useState<UserStatus>(
    userStatusPref || UserStatus.UNSPECIFIED
  );

  const [userSeatTypeFilter, setUserSeatTypeFilter] =
    useState<SubscriptionSeatTypes>(
      SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
    );

  const orgUsersListContextValue = { orgUsersList, setOrgUsersList };
  const adminOnlyFilterContextValue = { adminOnlyFilter, setAdminOnlyFilter };
  const userQueryFilterContextValue = { userQueryFilter, setUserQueryFilter };
  const userStatusFilterContextValue = {
    userStatusFilter,
    setUserStatusFilter,
  };
  const userSeatTypeFilterContextValue = {
    userSeatTypeFilter,
    setUserSeatTypeFilter,
  };
  return (
    <OrgUsersListContext.Provider value={orgUsersListContextValue}>
      <AdminOnlyFilterContext.Provider value={adminOnlyFilterContextValue}>
        <UserQueryFilterContext.Provider value={userQueryFilterContextValue}>
          <UserStatusFilterContext.Provider
            value={userStatusFilterContextValue}
          >
            <UserSeatTypeFilterContext.Provider
              value={userSeatTypeFilterContextValue}
            >
              {children}
            </UserSeatTypeFilterContext.Provider>
          </UserStatusFilterContext.Provider>
        </UserQueryFilterContext.Provider>
      </AdminOnlyFilterContext.Provider>
    </OrgUsersListContext.Provider>
  );
};

// Main organisation page data context
export const OrganisationContextWrapper = ({
  children,
}: OrganisationContextWrapperProps) => {
  const [orgDetails, setOrgDetails] = useState<RoleDetails>(Object);
  const [orgData, setOrgData] = useState<Role>(Object);
  const [subData, setSubData] = useState<GetSubscriptionResponse>(Object);
  const [orgHasSensors, setOrgHasSensors] = useState<Asset_ExistsResult>(
    Asset_ExistsResult.ASSET_NOT_EXISTS
  );
  const [isOrgAndSeatDataLoading, setIsOrgAndSeatDataLoading] =
    useState<boolean>(true);
  const [subscriptionSeatUsage, setSubscriptionSeatUsage] =
    useState<GetSubscriptionSeatUsageResponse>(Object);
  const [orgInspectionQuota, setOrgInspectionQuota] =
    useState<GetOrgInspectionQuotaResponse>(Object);

  const [contractsList, setContractsList] = useState<GetContractsByOrgResponse>(
    Object || {}
  );

  const orgDetailsContextValue = {
    orgDetails,
    setOrgDetails,
  };
  const orgDataContextValue = {
    orgData,
    setOrgData,
  };
  const subDataContextValue = {
    subData,
    setSubData,
  };
  const orgHasSensorsContextValue = { orgHasSensors, setOrgHasSensors };
  const isOrgAndSeatDataLoadingContextValue = {
    isOrgAndSeatDataLoading,
    setIsOrgAndSeatDataLoading,
  };
  const subscriptionSeatUsageContextValue = {
    subscriptionSeatUsage,
    setSubscriptionSeatUsage,
  };
  const orgInspectionQuotaContextValue = {
    orgInspectionQuota,
    setOrgInspectionQuota,
  };

  // This retrieves the current contract from the list of contracts. The backend returns a list of contracts in DESC order by created_at.
  const getCurrentContract = (contractsList: GetContractsByOrgResponse) => {
    return contractsList?.contracts?.[0] || [];
  };

  const [isContractListLoading, setIsContractListLoading] =
    useState<boolean>(true);

  const contractsListContextValue = {
    contractsList,
    setContractsList,
    getCurrentContract,
    setIsContractListLoading,
    isContractListLoading,
  };

  return (
    <OrgDetailsContext.Provider value={orgDetailsContextValue}>
      <OrgDataContext.Provider value={orgDataContextValue}>
        <SubDataContext.Provider value={subDataContextValue}>
          <ContractsListContext.Provider value={contractsListContextValue}>
            <OrgHasSensorsContext.Provider value={orgHasSensorsContextValue}>
              <OrgInspectionQuotaContext.Provider
                value={orgInspectionQuotaContextValue}
              >
                <IsOrgAndSeatDataLoadingContext.Provider
                  value={isOrgAndSeatDataLoadingContextValue}
                >
                  <SubscriptionSeatUsageContext.Provider
                    value={subscriptionSeatUsageContextValue}
                  >
                    <OrgUsersListContextWrapper>
                      {children}
                    </OrgUsersListContextWrapper>
                  </SubscriptionSeatUsageContext.Provider>
                </IsOrgAndSeatDataLoadingContext.Provider>
              </OrgInspectionQuotaContext.Provider>
            </OrgHasSensorsContext.Provider>
          </ContractsListContext.Provider>
        </SubDataContext.Provider>
      </OrgDataContext.Provider>
    </OrgDetailsContext.Provider>
  );
};

export default OrganisationContextWrapper;
